import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  activeOptionFilterSearchValueChange,
  applySingleFilter,
  applySingleFilterAction,
  clearFilters,
  clearFiltersInit,
  externalFiltersMenuCleaning,
  getFilterElementsList,
  getFilterElementsListFailed,
  getFilterElementsListSuccess,
  updateFiltersAction,
  updateFiltersData,
} from '../actions/filters.actions';
import { selectFiltersState, selectNumOfActiveFilters } from '../selectors/filters.selectors';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
  ActiveFiltersSelectionsTypes,
  FiltersOptions,
  FiltersResetTypes,
} from 'src/app/shared/enums';
import { FiltersOptionsType, FiltersRequestTypes } from '../models/filters.model';
import { IFilter } from 'src/app/shared/interfaces';
import { DepartmentsService } from 'src/app/shared/services/departments/departments.service';
import { UtilsService } from 'src/app/shared/services/utils.service';

@Injectable()
export class FiltersEffects {
  private _actions$ = inject(Actions);
  private _store = inject(Store);
  private _departmentsService = inject(DepartmentsService);
  private _utils = inject(UtilsService);

  public clearFiltersState$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(clearFiltersInit),
      concatLatestFrom(() => [
        this._store.select(selectFiltersState),
        this._store.select(selectNumOfActiveFilters),
      ]),
      switchMap(([action, chosenFilters, numOfActiveFilters]) => {
        const updateFiltersState = {
          filtersParams: { ...chosenFilters.filtersList },
          favs: chosenFilters.favs,
        };

        let options: FiltersOptionsType[] = [];
        let excludeFiltersOptions: FiltersOptions[] = [];

        if (chosenFilters.filtersList) {
          options = Object.keys(chosenFilters.filtersList) as FiltersOptionsType[];

          excludeFiltersOptions = this._utils.getOnlyExcludeFiltersOptions(
            chosenFilters.filtersList,
          );
        }

        if (
          (action.clearType === FiltersResetTypes.deleteChip ||
            action.clearType === FiltersResetTypes.resetCurrent) &&
          action.activeFilterOption
        ) {
          if (numOfActiveFilters === ActiveFiltersSelectionsTypes.mono) {
            updateFiltersState.filtersParams = this._utils.cleanMonoTypeFilters(
              updateFiltersState.filtersParams,
              options,
              action.activeFilterOption,
              action.id,
            );
          }

          if (numOfActiveFilters === ActiveFiltersSelectionsTypes.multiple) {
            updateFiltersState.filtersParams = this._utils.cleanMultiplyTypeFilters(
              updateFiltersState.filtersParams,
              options,
              excludeFiltersOptions,
              action.activeFilterOption,
            );
          }
        }

        if (action.clearType === FiltersResetTypes.resetAll) {
          options.forEach((option) => {
            updateFiltersState.filtersParams = this._utils.updateFiltersDataByOption(
              updateFiltersState.filtersParams,
              option,
            );
          });
          updateFiltersState.favs = false;
        }

        return of(
          clearFilters({
            update: updateFiltersState.filtersParams,
            updateFavs: updateFiltersState.favs,
            isNeedExternalFiltersMenuCleaning: action.isNeedExternalFiltersMenuCleaning,
          }),
        );
      }),
    );
  });

  public externalClearFiltersMenu$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(clearFilters),
      switchMap((data) => {
        if (data.isNeedExternalFiltersMenuCleaning) {
          return of(externalFiltersMenuCleaning({ ...data }));
        }
        return of();
      }),
    );
  });

  public getFilterElements$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(getFilterElementsList, activeOptionFilterSearchValueChange),
      concatLatestFrom(() => [
        this._store.select(selectFiltersState),
        this._store.select(selectNumOfActiveFilters),
      ]),
      switchMap(([action, filtersState, numOfActiveFilters]) => {
        let filters: IFilter = {
          page: filtersState.page,
          size: filtersState.size,
        };

        filters[
          action.activeFilterOption === FiltersOptions.cities ||
          action.activeFilterOption === FiltersOptions.groups
            ? 'name'
            : 'query'
        ] = action.type === activeOptionFilterSearchValueChange.type ? action.searchValue : null;

        if (action.activeFilterOption === FiltersOptions.groups) {
          filters.type = 'GROUP';
          filters.rootOnly = false;
        }

        if (
          numOfActiveFilters === ActiveFiltersSelectionsTypes.multiple &&
          action.activeFilterOption === FiltersOptions.shops
        ) {
          filters = {
            ...filters,
            region: this._utils.setRequestFilterParams({
              filtersOptionsParams: filtersState.filtersList,
              filterOption: FiltersOptions.regions,
            }),
            city: this._utils.setRequestFilterParams({
              filtersOptionsParams: filtersState.filtersList,
              filterOption: FiltersOptions.cities,
            }),
          };
        }

        const requestObservable$: Observable<FiltersRequestTypes> = this._departmentsService
          .getParamsForFilters(action.activeFilterOption, filters)
          .pipe(map((data) => data.elements));

        return requestObservable$.pipe(
          map((list) => {
            return getFilterElementsListSuccess({
              list,
              resetData: filtersState.page === 0,
              size: filtersState.size,
            });
          }),
          catchError((error) => {
            return of(getFilterElementsListFailed({ error }));
          }),
        );
      }),
    );
  });

  public updateFiltersState$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(updateFiltersAction),
      concatLatestFrom(() => this._store.select(selectNumOfActiveFilters)),
      switchMap(([action, numOfActiveFilters]) => {
        let filters = { ...action.filtersOptionsParams };
        let options: FiltersOptionsType[] = [];

        if (action.filtersOptionsParams) {
          options = Object.keys(action.filtersOptionsParams) as FiltersOptionsType[];
        }

        if (numOfActiveFilters === ActiveFiltersSelectionsTypes.mono && action.activeFilterOption) {
          options.forEach((option) => {
            if (
              option !== action.activeFilterOption &&
              !filters[option]?.isFilterSingleButton &&
              filters[action.activeFilterOption!]?.params?.length
            ) {
              filters = this._utils.updateFiltersDataByOption(filters, option);
            }
          });
        }

        if (
          numOfActiveFilters === ActiveFiltersSelectionsTypes.multiple &&
          action.activeFilterOption
        ) {
          filters = this._utils.updateFiltersWhenSelecting(
            filters,
            options,
            action.activeFilterOption,
          );
        }

        return of(updateFiltersData({ updateData: filters ?? null }));
      }),
    );
  });

  public updateFiltersStateBySingleFilter$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(applySingleFilterAction),
      concatLatestFrom(() => this._store.select(selectFiltersState)),
      switchMap(([action, filtersState]) => {
        const filters = { ...filtersState.filtersList, ...action.data };

        return of(applySingleFilter({ update: filters ?? null }));
      }),
    );
  });
}
